import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faArrowUpFromBracket,
  faArrowsRepeat,
  faPen,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  defineMessages,
  useIntl,
} from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ShopItemType } from '@sorare/core/src/__generated__/globalTypes';
import defaultShield from '@sorare/core/src/assets/club/shield_none.webp';
import type { Color } from '@sorare/core/src/atoms/buttons/Button';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { ActiveUserAvatar } from '@sorare/core/src/components/user/ActiveUserAvatar';
import { DiscordUser } from '@sorare/core/src/components/user/DiscordUser';
import { FollowButton } from '@sorare/core/src/components/user/FollowButton';
import { TwitterUser } from '@sorare/core/src/components/user/TwitterUser';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { NETWORK_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useDefaultStadiumBackground } from '@sorare/core/src/hooks/useDefaultStadiumBackground';
import { useUpdateSportProfile } from '@sorare/core/src/hooks/useUpdateSportProfile';
import { range } from '@sorare/core/src/lib/arrays';
import { fantasy, glossary, navLabels } from '@sorare/core/src/lib/glossary';
import {
  desktopAndAbove,
  socialPost,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { TradeButton } from '@sorare/marketplace/src/components/TradeButton';

import { ShopItemDialog } from 'components/shopItems/ShopItemDialog';

import { AchievementBadgePlaceholder } from './AchievementBadgePlaceholder';
import { ManagerIDStat } from './ManagerIDStat';
import { ResponsiveClubName } from './ResponsiveClubName';
import { ClubEditor } from '../Header/ClubEditor';
import { Share } from '../Header/Share';
import { Trophies } from '../Pro/Trophies';
import type {
  ManagerIDHeader_user,
  ManagerIDHeader_userSportProfile,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 0 })`
  border-radius: var(--double-unit);
  border: 2px solid var(--c-nd-100-opaque);
`;
const StadiumRoot = styled(Vertical)`
  justify-content: space-between;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  flex: 1;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: no-repeat center / cover var(--bg);
    filter: brightness(0.6);
  }

  & > * {
    position: relative;
  }
`;
const StyledButton = styled(Button)`
  min-width: auto;

  &.iconOnly {
    width: var(--triple-unit);

    svg {
      height: 1em;
      overflow: visible;
    }
  }
`;
const Grid = styled.div`
  --achievement-badge-size: 32px;
  @media ${tabletAndAbove} {
    --achievement-badge-size: 64px;
  }

  display: grid;
  gap: var(--double-unit);
  grid-template-columns: var(--achievement-badge-size) 1fr;
  grid-template-areas:
    'badges infos'
    'badges stats';

  @media ${desktopAndAbove} {
    grid-template-columns: var(--achievement-badge-size) 1fr var(
        --achievement-badge-size
      );
    grid-template-areas:
      'badges infos .'
      'badges stats .';
  }

  .socialSharing & {
    --achievement-badge-size: 120px;

    @media ${socialPost} {
      --achievement-badge-size: 80px;
    }
  }

  .vertical & {
    grid-template-columns: var(--achievement-badge-size) 1fr var(
        --achievement-badge-size
      );
    grid-template-columns: 1fr;
    grid-template-areas:
      'infos' 'badges'
      'stats';

    .socialSharing & {
      grid-template-columns: 1fr;
      gap: var(--quadruple-unit);
    }
  }
`;
const Badges = styled(Horizontal)`
  grid-area: badges;
  flex-direction: column;
  justify-content: space-between;

  .vertical & {
    justify-content: center;
    flex-direction: row;
  }
`;
const AchievementBadge = styled.img`
  width: var(--achievement-badge-size);
  aspect-ratio: 1/1;
  object-fit: contain;
`;
const ClubBadge = styled.img`
  width: 80px;
  aspect-ratio: 1/1;
  margin: auto;

  .socialSharing & {
    width: 240px;

    @media ${socialPost} {
      width: 160px;
    }
  }
`;
const Infos = styled(Vertical)`
  grid-area: infos;
  overflow: hidden;
`;
const ClubBadgeWrapper = styled(Vertical)`
  overflow: hidden;
`;
const Group = styled(Horizontal)`
  flex-wrap: wrap;
  justify-content: center;
`;
const StatsContainer = styled(Vertical)`
  grid-area: stats;
`;
const InnerStatsContainer = styled(SBHorizontal).attrs({ gap: 3 })`
  width: 100%;
  align-self: center;
  align-items: flex-start;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  background: var(--c-nl-300);
  backdrop-filter: blur(16px);
  & > * {
    flex: 1;
  }

  @media ${tabletAndAbove} {
    width: 75%;
  }
`;
const TrophiesWrapper = styled.div`
  width: 50%;
`;
const AvatarAndName = styled(Horizontal)`
  overflow: hidden;
`;
const StyledBodyS = styled(BodyS)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const messages = defineMessages({
  joined: {
    id: 'ManagerProfile.Header.joined',
    defaultMessage: 'Joined',
  },
});

type Props = {
  user: ManagerIDHeader_user;
  sport: Sport;
  userSportProfile: ManagerIDHeader_userSportProfile | null;
  managerProfilePath?: string;
  galleryPath?: string;
  hideActions?: boolean;
  verticalLayout?: boolean;
  socialSharing?: boolean;
  className?: string;
};
export const ManagerIDHeader = ({
  user,
  sport,
  userSportProfile,
  managerProfilePath,
  galleryPath,
  className,
  hideActions,
  verticalLayout,
  socialSharing,
}: Props) => {
  const { currentUser, isMe } = useCurrentUserContext() || {
    isMe() {
      return false;
    },
  };
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { up: isPost } = useScreenSize('post');
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext() || {};
  const updateSportProfile = useUpdateSportProfile();
  const [openClubEditor, setOpenClubEditor] = useState(false);
  const [editingAchievmentBadgeIndex, setEditingAchievmentBadgeIndex] =
    useState<number>();
  const [editingClubBadge, setEditingClubBadge] = useState(false);
  const defaultStadiumBackground = useDefaultStadiumBackground(sport);

  const isTabletAndAbove = useIsTabletAndAbove();

  const isMeUser = isMe(user);

  useEffect(() => {
    if (setPageMetadata && userSportProfile?.managerProfileSocialPictureUrls) {
      return setPageMetadata(
        userSportProfile.clubName ||
          formatMessage(navLabels.userClub, {
            username: user.nickname,
          }),
        {
          img: userSportProfile?.managerProfileSocialPictureUrls?.post,
        }
      );
    }
    return () => {};
  }, [setPageMetadata, formatMessage, userSportProfile, user.nickname]);

  if (!userSportProfile) return null;

  const socialSharingAvatarSize = isPost ? 5 : 8;
  const avatarSize = socialSharing ? socialSharingAvatarSize : 3;
  return (
    <Root
      className={classNames(className, {
        socialSharing,
        vertical: !isLaptopAndAbove || verticalLayout,
      })}
    >
      <StadiumRoot
        gap={socialSharing ? 4 : 2}
        style={
          {
            '--bg': `url(${
              userSportProfile.background?.pictureUrl ||
              defaultStadiumBackground
            })`,
          } as CSSProperties
        }
      >
        <SBHorizontal>
          <AvatarAndName>
            <ActiveUserAvatar
              user={user}
              editable={currentUser?.slug === user.slug}
              size={avatarSize}
              hideChampionshipFrame
            />
            <StyledBodyS as="p">
              <UserName user={user} />
            </StyledBodyS>
          </AvatarAndName>
          <Horizontal>
            {!hideActions && (
              <>
                {!!managerProfilePath && !!galleryPath && !!isMeUser && (
                  <Share
                    managerProfilePath={managerProfilePath}
                    galleryPath={galleryPath}
                    managerProfileSocialPictures={
                      userSportProfile.managerProfileSocialPictureUrls
                    }
                    gallerySocialPictures={
                      userSportProfile.gallerySocialPictureUrls
                    }
                    renderButton={({ onClick }) => (
                      <StyledButton
                        color="quaternary"
                        size="small"
                        className={classNames({ iconOnly: !isLaptopAndAbove })}
                        onClick={onClick}
                      >
                        <FontAwesomeIcon icon={faArrowUpFromBracket} />
                        {!!isLaptopAndAbove && (
                          <span>
                            <FormattedMessage {...glossary.share} />
                          </span>
                        )}
                      </StyledButton>
                    )}
                    fingerprint={userSportProfile.socialPictureFingerprint}
                  />
                )}
                {!isMeUser && (
                  <>
                    <TradeButton
                      user={user}
                      renderButton={({ onClick }) => (
                        <StyledButton
                          color="quaternary"
                          size="small"
                          className={classNames({
                            iconOnly: !isLaptopAndAbove,
                          })}
                          onClick={onClick}
                        >
                          <FontAwesomeIcon icon={faArrowsRepeat} />
                          {!!isLaptopAndAbove && (
                            <span>
                              <FormattedMessage {...glossary.trade} />
                            </span>
                          )}
                        </StyledButton>
                      )}
                    />
                    <FollowButton
                      size="small"
                      subscribable={{ __typename: 'User', slug: user.slug }}
                      initialSubscription={user.followed}
                      renderButton={(buttonProps, subscription, hovered) => {
                        let followButtonColor: Color = 'quaternary';
                        if (subscription && hovered) {
                          followButtonColor = 'red';
                        } else if (subscription) {
                          followButtonColor = 'secondary';
                        }

                        return (
                          <StyledButton
                            color={followButtonColor}
                            size="small"
                            {...buttonProps}
                          >
                            {subscription ? (
                              <FormattedMessage
                                {...(hovered
                                  ? fantasy.removeCircle
                                  : fantasy.inCircle)}
                              />
                            ) : (
                              <FormattedMessage {...fantasy.addCircle} />
                            )}
                          </StyledButton>
                        );
                      }}
                    />
                  </>
                )}
                {!!isMeUser && (
                  <>
                    <StyledButton
                      color="quaternary"
                      size="small"
                      className={classNames({ iconOnly: !isLaptopAndAbove })}
                      onClick={() => setOpenClubEditor(true)}
                    >
                      <FontAwesomeIcon icon={faPen} />
                      {!!isLaptopAndAbove && (
                        <span>
                          <FormattedMessage {...glossary.edit} />
                        </span>
                      )}
                    </StyledButton>
                    <ClubEditor
                      open={openClubEditor}
                      onClose={() => setOpenClubEditor(false)}
                      onSubmit={async ({
                        clubName,
                        selectedStatistics,
                        backgroundId,
                        clubBadgeId,
                        achievementBadgesIds,
                      }) =>
                        updateSportProfile({
                          sport,
                          clubName,
                          selectedStatistics,
                          backgroundId,
                          clubBadgeId,
                          achievementBadgesIds,
                        })
                      }
                      userSportProfile={userSportProfile}
                    />
                  </>
                )}
              </>
            )}
          </Horizontal>
        </SBHorizontal>
        <Grid>
          <Badges center gap={socialSharing ? 6 : 2}>
            {range(3).map(i => {
              const badge = userSportProfile.achievementBadges?.[i];
              // eslint-disable-next-line react/no-array-index-key
              return (
                <Tooltip
                  key={i}
                  title={badge?.description || ''}
                  placement={isTabletAndAbove ? 'right' : 'bottom'}
                >
                  <ButtonBase
                    disabled={hideActions || !isMeUser}
                    onClick={() => setEditingAchievmentBadgeIndex(i)}
                  >
                    {badge?.pictureUrl ? (
                      <AchievementBadge
                        src={badge?.pictureUrl}
                        alt={badge?.name || ''}
                      />
                    ) : (
                      <AchievementBadgePlaceholder displayPlus={isMeUser} />
                    )}
                  </ButtonBase>
                </Tooltip>
              );
            })}
          </Badges>
          <Infos gap={socialSharing ? 3 : 2}>
            <ClubBadgeWrapper center>
              <ButtonBase
                disabled={hideActions || !isMeUser}
                onClick={() => setEditingClubBadge(true)}
              >
                <ClubBadge
                  src={userSportProfile.clubBadge?.pictureUrl || defaultShield}
                  alt={userSportProfile.clubBadge?.name || ''}
                />
              </ButtonBase>
              <ResponsiveClubName name={userSportProfile.clubName} />
            </ClubBadgeWrapper>
            <Vertical gap={socialSharing ? 2 : 1} center>
              <Horizontal gap={2}>
                {!!user.profile.twitterUsername && (
                  <BodyS as="div" color="var(--c-white)">
                    <TwitterUser userProfile={user.profile} />
                  </BodyS>
                )}
                {!!user.profile.discordUsername && (
                  <BodyS as="div" color="var(--c-white)">
                    <DiscordUser userProfile={user.profile} />
                  </BodyS>
                )}
              </Horizontal>
              <Horizontal gap={socialSharing ? 4 : 2}>
                <Link
                  to={`${generatePath(NETWORK_SLUG, {
                    slug: user.slug,
                  })}?tab=followers`}
                >
                  <Group gap={0.5}>
                    <BodyS as="p" color="var(--c-white)">
                      <FormattedNumber
                        value={user.followersCount}
                        compactDisplay="short"
                        notation="compact"
                        maximumFractionDigits={1}
                      />
                    </BodyS>
                    <LabelS as="p" color="var(--c-nd-600-opaque)">
                      <FormattedMessage {...glossary.followers} />
                    </LabelS>
                  </Group>
                </Link>
                <Link
                  to={`${generatePath(NETWORK_SLUG, {
                    slug: user.slug,
                  })}?tab=following`}
                >
                  <Group gap={0.5}>
                    <BodyS as="p" color="var(--c-white)">
                      <FormattedNumber
                        value={user.followingCount}
                        compactDisplay="short"
                        notation="compact"
                        maximumFractionDigits={1}
                      />
                    </BodyS>
                    <LabelS as="p" color="var(--c-nd-600-opaque)">
                      <FormattedMessage {...glossary.following} />
                    </LabelS>
                  </Group>
                </Link>
                <Group gap={0.5}>
                  <BodyS as="p" color="var(--c-white)">
                    <FormattedDate
                      value={user.createdAt}
                      month="2-digit"
                      day="2-digit"
                      year="numeric"
                    />
                  </BodyS>
                  <LabelS as="p" color="var(--c-nd-600-opaque)">
                    <FormattedMessage {...messages.joined} />
                  </LabelS>
                </Group>
              </Horizontal>
            </Vertical>
          </Infos>
          <StatsContainer center>
            <InnerStatsContainer>
              {range(3).map(
                i =>
                  userSportProfile.selectedUserSportStatistics?.[i] && (
                    <ManagerIDStat
                      key={
                        userSportProfile.selectedUserSportStatistics?.[i]?.id
                      }
                      statistic={
                        userSportProfile.selectedUserSportStatistics[i]
                      }
                    />
                  )
              )}
            </InnerStatsContainer>
            <TrophiesWrapper>
              <Trophies trophies={user.so5TrophiesSummary} />
            </TrophiesWrapper>
          </StatsContainer>
        </Grid>
      </StadiumRoot>
      {!!(editingAchievmentBadgeIndex !== undefined || editingClubBadge) && (
        <ShopItemDialog
          open
          onClose={() => {
            setEditingAchievmentBadgeIndex(undefined);
            setEditingClubBadge(false);
          }}
          type={
            editingClubBadge
              ? ShopItemType.CLUB_BADGE
              : ShopItemType.ACHIEVEMENT_BADGE
          }
          onSelect={({ id }) => {
            if (editingClubBadge) {
              updateSportProfile({
                sport,
                clubBadgeId: id,
              });
              setEditingClubBadge(false);
            } else if (editingAchievmentBadgeIndex !== undefined) {
              const newBadges = [
                ...(userSportProfile.achievementBadges?.map(
                  badge => badge?.id || null
                ) || []),
              ];
              newBadges[editingAchievmentBadgeIndex] = id;
              updateSportProfile({
                sport,
                achievementBadgesIds: newBadges,
              });
              setEditingAchievmentBadgeIndex(undefined);
            }
          }}
        />
      )}
    </Root>
  );
};

ManagerIDHeader.fragments = {
  user: gql`
    fragment ManagerIDHeader_user on User {
      id
      slug
      nickname
      createdAt
      followersCount
      followingCount
      so5TrophiesSummary(sport: $sport) {
        top1: podiumRankings(ranking: 1)
        top2: podiumRankings(ranking: 2)
        top3: podiumRankings(ranking: 3)
      }
      followed {
        slug
      }
      profile {
        id
        status
        discordUsername
        twitterUsername
      }
      ...ActiveUserAvatar_user
      ...UserName_publicUserInfoInterface
      ...TradeButton_publicUserInfoInterface
    }
    ${ActiveUserAvatar.fragments.user}
    ${UserName.fragments.user}
    ${TradeButton.fragments.user}
  ` as TypedDocumentNode<ManagerIDHeader_user>,
  userSportProfile: gql`
    fragment ManagerIDHeader_userSportProfile on UserSportProfileInterface {
      id
      clubName
      background {
        id
        pictureUrl
        name
      }
      clubBadge {
        id
        pictureUrl
        name
      }
      achievementBadges {
        id
        pictureUrl
        name
        description
      }
      socialPictureFingerprint
      managerProfileSocialPictureUrls {
        ...Share_socialPictureDerivative
      }
      gallerySocialPictureUrls {
        ...Share_socialPictureDerivative
      }
      highlightedDeck {
        slug
        name
        socialPictureUrls {
          ...Share_socialPictureDerivative
        }
      }
      selectedUserSportStatistics {
        id
        name
        title
        ...ManagerIDStat_userSportStatistic
      }
      ...ClubEditor_userSportProfile
    }
    ${Share.fragments.socialPictureDerivative}
    ${ClubEditor.fragments.userSportProfile}
    ${ManagerIDStat.fragments.userSportStatistic}
  ` as TypedDocumentNode<ManagerIDHeader_userSportProfile>,
};
